
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check } from "lucide-react";

export interface PricingTier {
  id: string;
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  cta: string;
  comingSoon?: boolean;
  isBeta?: boolean;
  label?: string;
  isLifetime?: boolean;
}

export const pricingTiers: PricingTier[] = [
  {
    id: "price_1QhOtZCFL931KD1uLd6J00IU",
    name: "Monthly",
    price: "$19.99",
    description: "Best for regular strategic planning",
    features: [
      "Unlimited document creation",
      "AI-powered business analysis",
      "Export to PDF/Word",
      "Email support",
      "Basic templates library"
    ],
    cta: "Start Monthly Plan",
    label: "Most Popular"
  },
  {
    id: "price_1RcZDiCFL931KD1uLt5k7pgD",
    name: "Team",
    price: "$99",
    description: "Perfect for small teams (up to 5 seats)",
    features: [
      "Everything in Monthly",
      "Team collaboration features",
      "Up to 5 seats included",
      "Advanced templates",
      "Priority support",
      "Team analytics dashboard"
    ],
    cta: "Start Team Plan",
    isBeta: true,
    label: "Best for Startups"
  },
  {
    id: "price_1RcZFaCFL931KD1uSC8jy4Mp",
    name: "Enterprise",
    price: "$399",
    description: "For large organizations and consultants (up to 100 seats)",
    features: [
      "Everything in Team",
      "Up to 100 seats included",
      "White-label branding",
      "Custom subdomain",
      "Advanced analytics",
      "Dedicated account manager",
      "Custom integrations"
    ],
    cta: "Start Enterprise Plan",
    isBeta: true,
    label: "Best for Consultants"
  },
  {
    id: "price_1Q8QzHCFL931KD1u55LEohO0",
    name: "Lifetime",
    price: "$999",
    description: "Limited time offer - pay once, use forever (998 left)",
    features: [
      "Everything in Monthly",
      "Lifetime access",
      "All future updates",
      "Premium support",
      "Early access to new features"
    ],
    cta: "Get Lifetime Access",
    label: "Best Value",
    isLifetime: true
  }
];

export const PricingPlanCard = ({ tier }: { tier: PricingTier }) => {
  return (
    <Card className="relative border-gray-200 h-full flex flex-col">
      {tier.label && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <Badge className="bg-cso-600 text-white whitespace-nowrap">{tier.label}</Badge>
        </div>
      )}
      
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-bold">{tier.name}</CardTitle>
          {tier.isBeta && (
            <Badge variant="secondary" className="bg-orange-100 text-orange-800">
              BETA
            </Badge>
          )}
        </div>
        <div className="text-3xl font-bold">
          {tier.price}
          {!tier.isLifetime && (
            <span className="text-sm font-normal text-gray-600">/month</span>
          )}
        </div>
        <p className="text-gray-600 min-h-[3rem] flex items-center">{tier.description}</p>
      </CardHeader>
      
      <CardContent className="flex-grow">
        <ul className="space-y-3">
          {tier.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
